import { useRef, useEffect } from 'react';

import { PointQueryFilter, PointQueryPagination, PointQuerySort } from '@shared/api/graphql/generated';
import { DEFAULT_SHORT_PAGINATION, DEFAULT_PAGINATION_INFO } from 'application/pages/constants';
import { useAccountCompanyIds } from 'application/hooks';

import {
  POINTS_PAGINATION_DEFAULT_FILTERS,
  UsePointsPaginationPresenterProps,
  UsePointsPaginationPresenter,
} from './types';

const usePointPaginationPresenter = ({
  getPoints,
  getMorePoints,
  readPoints,
  hasVideoSupport,
}: UsePointsPaginationPresenterProps): UsePointsPaginationPresenter => {
  const companyIds = useAccountCompanyIds();

  const filtersRef = useRef<PointQueryFilter>({
    ...POINTS_PAGINATION_DEFAULT_FILTERS,
    hasVideoSupport,
    companyIds,
  });

  const paginationRef = useRef<PointQueryPagination>(DEFAULT_SHORT_PAGINATION);

  const sortRef = useRef<PointQuerySort>(PointQuerySort.IsVideoDemoDesc);

  const allPointsCount = useRef<null | number>(null);

  const cachedPoints = readPoints({
    filters: filtersRef.current,
    pagination: paginationRef.current,
    sort: sortRef.current,
  });

  const resetFilters = () => {
    filtersRef.current = {
      ...POINTS_PAGINATION_DEFAULT_FILTERS,
      hasVideoSupport,
      companyIds,
    };

    paginationRef.current = DEFAULT_SHORT_PAGINATION;

    sortRef.current = PointQuerySort.IsVideoDemoDesc;
  };

  const requestPoints = async (
    filters: PointQueryFilter = POINTS_PAGINATION_DEFAULT_FILTERS,
    pagination: PointQueryPagination = DEFAULT_SHORT_PAGINATION
  ) => {
    filtersRef.current = {
      ...filtersRef.current,
      ...filters,
      hasVideoSupport,
      companyIds,
    };

    paginationRef.current = {
      ...paginationRef.current,
      ...pagination,
    };

    await getPoints({
      filters: filtersRef.current,
      pagination: paginationRef.current,
      sort: sortRef.current,
    });
  };

  const loadMorePoints = async () => {
    const page = (cachedPoints?.paginationInfo?.page || 0) + 1;

    if (page <= paginationRef.current.page) return;

    paginationRef.current.page = page;

    await getMorePoints({
      filters: filtersRef.current,
      pagination: paginationRef.current,
      sort: sortRef.current,
    });
  };

  const onFilterExecute = async (newFilters: PointQueryFilter) => {
    const preparedFilters: PointQueryFilter = {
      companyIds,
    };

    const { query, companyTagIds } = newFilters;

    if (query !== undefined) {
      preparedFilters.query = query;
      filtersRef.current.query = query;
    }

    if (preparedFilters.query === undefined && filtersRef.current.query) {
      preparedFilters.query = filtersRef.current.query;
    }

    if (companyTagIds !== undefined) {
      preparedFilters.companyTagIds = companyTagIds;
      filtersRef.current.companyTagIds = companyTagIds;
    }

    if (preparedFilters.companyTagIds === undefined && filtersRef.current.companyTagIds) {
      preparedFilters.companyTagIds = filtersRef.current.companyTagIds;
    }

    if (preparedFilters.playingStatuses === undefined && filtersRef.current.playingStatuses) {
      preparedFilters.playingStatuses = filtersRef.current.playingStatuses;
    }

    await requestPoints(preparedFilters);
  };

  useEffect(() => {
    if (
      cachedPoints?.paginationInfo?.totalCount !== undefined &&
      (allPointsCount.current === null || allPointsCount.current === 0)
    ) {
      allPointsCount.current = cachedPoints.paginationInfo?.totalCount || 0;
    }
  }, [cachedPoints?.paginationInfo]);

  return {
    points: cachedPoints?.result || [],
    paginationInfo: cachedPoints?.paginationInfo || DEFAULT_PAGINATION_INFO,
    allPointsCount: allPointsCount.current,
    filtersRef,
    requestPoints,
    loadMorePoints,
    resetFilters,
    onFilterExecute,
  };
};

export default usePointPaginationPresenter;
